import { API } from '@/config/api';
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Syringe, Save, Calculator as CalcIcon } from 'lucide-react';
import { toast } from 'sonner';


export default function Calculator() {
  const [vialAmount, setVialAmount] = useState(5);
  const [waterMl, setWaterMl] = useState(2);
  const [desiredDose, setDesiredDose] = useState(250);
  const [doseUnit, setDoseUnit] = useState('mcg');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/calculator/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          peptide_ids: [],
          vial_amount_mg: parseFloat(vialAmount),
          water_ml: parseFloat(waterMl),
          desired_dose: parseFloat(desiredDose),
          desired_dose_unit: doseUnit
        })
      });

      if (!response.ok) throw new Error('Calculation failed');

      const data = await response.json();
      setResult(data);
    } catch (error) {
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API}/calculator/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          peptide_ids: [],
          vial_amount_mg: parseFloat(vialAmount),
          water_ml: parseFloat(waterMl),
          desired_dose: parseFloat(desiredDose),
          desired_dose_unit: doseUnit
        })
      });

      if (!response.ok) throw new Error('Save failed');

      toast.success('Calculation saved to favorites');
    } catch (error) {
      toast.error('Failed to save calculation');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="calculator-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
          Reconstitution Calculator
        </h1>
        <p className="text-muted-foreground">
          Calculate precise dosing for your research peptides
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Card */}
        <Card className="p-6 glass-card">
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <CalcIcon className="h-6 w-6 mr-2 text-primary" />
            Parameters
          </h2>

          <div className="space-y-6">
            {/* Vial Amount */}
            <div>
              <Label htmlFor="vial-amount" className="text-base">
                Vial Amount (mg)
              </Label>
              <div className="mt-3 space-y-3">
                <Slider
                  id="vial-amount"
                  value={[vialAmount]}
                  onValueChange={(value) => setVialAmount(value[0])}
                  min={1}
                  max={100}
                  step={1}
                  className="w-full"
                  data-testid="vial-amount-slider"
                />
                <Input
                  type="number"
                  value={vialAmount}
                  onChange={(e) => setVialAmount(e.target.value)}
                  className="font-mono"
                  data-testid="vial-amount-input"
                />
              </div>
            </div>

            {/* Bacteriostatic Water */}
            <div>
              <Label htmlFor="water-ml" className="text-base">
                Bacteriostatic Water (ml)
              </Label>
              <div className="mt-3 space-y-3">
                <Slider
                  id="water-ml"
                  value={[waterMl]}
                  onValueChange={(value) => setWaterMl(value[0])}
                  min={0.5}
                  max={5}
                  step={0.1}
                  className="w-full"
                  data-testid="water-ml-slider"
                />
                <Input
                  type="number"
                  step="0.1"
                  value={waterMl}
                  onChange={(e) => setWaterMl(e.target.value)}
                  className="font-mono"
                  data-testid="water-ml-input"
                />
              </div>
            </div>

            {/* Desired Dose */}
            <div>
              <Label htmlFor="desired-dose" className="text-base">
                Desired Dose
              </Label>
              <div className="mt-3 flex gap-2">
                <Input
                  type="number"
                  id="desired-dose"
                  value={desiredDose}
                  onChange={(e) => setDesiredDose(e.target.value)}
                  className="font-mono flex-1"
                  data-testid="desired-dose-input"
                />
                <Select value={doseUnit} onValueChange={setDoseUnit}>
                  <SelectTrigger className="w-24" data-testid="dose-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mcg">mcg</SelectItem>
                    <SelectItem value="mg">mg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleCalculate}
              disabled={loading}
              className="w-full"
              data-testid="calculate-button"
            >
              {loading ? 'Calculating...' : 'Calculate'}
            </Button>
          </div>
        </Card>

        {/* Results Card */}
        <Card className="p-6 glass-card">
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <Syringe className="h-6 w-6 mr-2 text-success" />
            Results
          </h2>

          {!result ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              Enter parameters and click Calculate
            </div>
          ) : (
            <div className="space-y-6" data-testid="calculator-results">
              {/* Concentration */}
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                <p className="text-sm text-muted-foreground mb-1">Concentration</p>
                <p className="text-3xl font-mono font-bold text-primary">
                  {result.concentration.toFixed(2)} mcg/ml
                </p>
              </div>

              {/* Volume to Draw */}
              <div className="p-4 rounded-lg bg-success/10 border border-success/30">
                <p className="text-sm text-muted-foreground mb-1">Volume to Draw</p>
                <p className="text-3xl font-mono font-bold text-success">
                  {result.volume_to_draw_ml.toFixed(4)} ml
                </p>
              </div>

              {/* Syringe Units */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">
                  Syringe Units (U-100 Insulin Syringes)
                </p>
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary">
                    <span className="text-sm">0.3ml (30 Units)</span>
                    <span className="font-mono font-bold">
                      {result.syringe_units['0.3ml_30U']} units
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary">
                    <span className="text-sm">0.5ml (50 Units)</span>
                    <span className="font-mono font-bold">
                      {result.syringe_units['0.5ml_50U']} units
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary">
                    <span className="text-sm">1.0ml (100 Units)</span>
                    <span className="font-mono font-bold">
                      {result.syringe_units['1ml_100U']} units
                    </span>
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={handleSave}
                className="w-full"
                data-testid="save-calculation-button"
              >
                <Save className="h-4 w-4 mr-2" />
                Save to Favorites
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Instructions */}
      <Card className="mt-6 p-6 glass-card">
        <h3 className="text-lg font-bold mb-4">How to Use</h3>
        <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
          <li>Enter your vial amount in mg (e.g., 5mg)</li>
          <li>Enter the amount of bacteriostatic water you'll add in ml (e.g., 2ml)</li>
          <li>Enter your desired dose per injection in mcg or mg (e.g., 250mcg)</li>
          <li>Click Calculate to see the exact volume to draw and syringe units</li>
          <li>Use the syringe unit values to accurately measure your dose</li>
        </ol>
      </Card>
    </div>
  );
}
